/**
 * THE INVISIBLE GUIDE
 * ===================
 * SUPREME MASTER SYSTEM PROTOCOL
 * 
 * ZERO-BOX PROTOCOL:
 * - NO text boxes, chat bubbles, or dialogue boxes
 * - NO buttons for user interaction
 * - Guide reads existing lesson text ONLY
 * - Voice-first interaction with Full-Duplex Voice
 * - Narration Mode: AI reads existing screen text, no side-chat
 * 
 * ASCENSION PATH VOICES:
 * - K-2: shimmer (gentle, patient)
 * - 3-5: nova (encouraging)
 * - 6-8: echo (clear, engaging)
 * - 9-12: coral (mature)
 * - College: onyx (scholarly)
 * - Master: sage (wise)
 * - Ordination: fable (pastoral)
 */

import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { isYoungLearnerGrade } from "../../utils/gradeUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// THE INVISIBLE GUIDE - One unified entity
const THE_GUIDE = {
  name: "The Guide",
  avatar: "📜",
  color: "#FFD700",
};

// Voice maturity mapping
const getVoice = (gradeLevel) => {
  if (!gradeLevel) return "echo";
  const level = gradeLevel.toUpperCase();
  if (level.includes("K") || level.includes("1") || level.includes("2")) return "shimmer";
  if (level.includes("3") || level.includes("4") || level.includes("5")) return "nova";
  if (level.includes("6") || level.includes("7") || level.includes("8")) return "echo";
  if (level.includes("9") || level.includes("10") || level.includes("11") || level.includes("12")) return "coral";
  if (level.includes("COLLEGE")) return "onyx";
  if (level.includes("MASTER")) return "sage";
  if (level.includes("ORDINATION")) return "fable";
  return "echo";
};

/**
 * InvisibleGuide Component
 * 
 * This component is truly INVISIBLE - it renders nothing visible.
 * It only:
 * 1. Monitors lesson content on screen
 * 2. Auto-reads content aloud (for K-2 mandatory, optional for others)
 * 3. Responds to voice commands (via Web Speech API)
 */
export function InvisibleGuide({ 
  gradeLevel, 
  lessonContent,
  isExamMode = false,
  user,
  onSpeakingChange
}) {
  const [isReading, setIsReading] = useState(false);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [autoReadEnabled, setAutoReadEnabled] = useState(false);
  const audioRef = useRef(null);
  const recognitionRef = useRef(null);
  const lastContentRef = useRef("");
  
  const voice = getVoice(gradeLevel);
  // FIXED 2026-04-25: was substring `.includes("1")` / `.includes("2")` —
  // falsely matched Grades 11/12/9-12 → high-schoolers got auto-read on.
  const isYoungLearner = isYoungLearnerGrade(gradeLevel);
  
  // Initialize auto-read for young learners
  useEffect(() => {
    if (isYoungLearner) {
      setAutoReadEnabled(true);
    }
  }, [isYoungLearner]);
  
  // Auto-read lesson content when it changes
  useEffect(() => {
    if (!autoReadEnabled || !lessonContent || isExamMode) return;
    if (lessonContent === lastContentRef.current) return;
    
    lastContentRef.current = lessonContent;
    readContent(lessonContent);
  }, [lessonContent, autoReadEnabled, isExamMode]);
  
  // Read content aloud using Voice-First API
  const readContent = useCallback(async (text) => {
    if (!text || isReading) return;
    
    try {
      setIsReading(true);
      onSpeakingChange?.(true);
      
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/classroom/voice-first/read`,
        {
          content_text: text.substring(0, 4000), // TTS limit
          grade_level: gradeLevel || "6-8",
          is_examination: isExamMode,
          section_index: 0,
          total_sections: 1
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data?.audio_url) {
        // Stop any existing audio
        if (audioRef.current) {
          audioRef.current.pause();
        }
        
        // Play new audio
        const audio = new Audio(response.data.audio_url);
        audioRef.current = audio;
        
        audio.onended = () => {
          setIsReading(false);
          onSpeakingChange?.(false);
        };
        
        audio.onerror = () => {
          setIsReading(false);
          onSpeakingChange?.(false);
        };
        
        await audio.play();
      }
    } catch (error) {
      console.error("Voice-First read error:", error);
      setIsReading(false);
      onSpeakingChange?.(false);
    }
  }, [gradeLevel, isExamMode, isReading, onSpeakingChange]);
  
  // Stop reading
  const stopReading = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsReading(false);
    onSpeakingChange?.(false);
  }, [onSpeakingChange]);
  
  // Initialize voice recognition for Full-Duplex Voice
  useEffect(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      console.log("Voice recognition not supported");
      return;
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    
    recognition.onresult = (event) => {
      const command = event.results[event.results.length - 1][0].transcript.toLowerCase();
      
      // Voice commands
      if (command.includes("read") || command.includes("teach")) {
        if (lessonContent) {
          readContent(lessonContent);
        }
      } else if (command.includes("stop") || command.includes("pause")) {
        stopReading();
      } else if (command.includes("repeat")) {
        if (lastContentRef.current) {
          readContent(lastContentRef.current);
        }
      }
    };
    
    recognition.onerror = (event) => {
      console.log("Voice recognition error:", event.error);
    };
    
    recognitionRef.current = recognition;
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [lessonContent, readContent, stopReading]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);
  
  // THE GUIDE IS TRULY INVISIBLE - NO UI RENDERED
  // Only a minimal floating indicator when reading (can be toggled off)
  if (!isReading) return null;
  
  return (
    <div 
      style={{
        position: "fixed",
        bottom: "20px",
        right: "20px",
        background: "rgba(0,0,0,0.8)",
        borderRadius: "50%",
        width: "50px",
        height: "50px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        zIndex: 9999,
        boxShadow: "0 4px 20px rgba(255,215,0,0.3)",
        border: "2px solid #FFD700",
        animation: "pulse 2s infinite"
      }}
      onClick={stopReading}
      title="Click to stop reading"
    >
      <span style={{ fontSize: "1.5rem" }}>🔊</span>
      <style>
        {`
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.1); }
          }
        `}
      </style>
    </div>
  );
}

/**
 * AutoTeacher Component
 * 
 * Automatically teaches lesson content paragraph by paragraph.
 * No visible UI - works in the background.
 */
export function AutoTeacher({
  gradeLevel,
  lessonContent,
  isEnabled,
  onProgress,
  onComplete
}) {
  const [currentParagraph, setCurrentParagraph] = useState(0);
  const [paragraphs, setParagraphs] = useState([]);
  const [isTeaching, setIsTeaching] = useState(false);
  const audioRef = useRef(null);
  
  const voice = getVoice(gradeLevel);
  
  // Parse lesson content into paragraphs
  useEffect(() => {
    if (!lessonContent) return;
    
    const parsed = lessonContent
      .split(/\n\n+/)
      .filter(p => p.trim().length > 20);
    
    setParagraphs(parsed);
    setCurrentParagraph(0);
  }, [lessonContent]);
  
  // Auto-teach when enabled
  useEffect(() => {
    if (!isEnabled || paragraphs.length === 0 || isTeaching) return;
    
    teachNextParagraph();
  }, [isEnabled, paragraphs]);
  
  const teachNextParagraph = async () => {
    if (currentParagraph >= paragraphs.length) {
      onComplete?.();
      return;
    }
    
    setIsTeaching(true);
    onProgress?.(currentParagraph, paragraphs.length);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/classroom/voice-first/read`,
        {
          content_text: paragraphs[currentParagraph],
          grade_level: gradeLevel || "6-8",
          is_examination: false,
          section_index: currentParagraph,
          total_sections: paragraphs.length
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data?.audio_url) {
        const audio = new Audio(response.data.audio_url);
        audioRef.current = audio;
        
        audio.onended = () => {
          setIsTeaching(false);
          setCurrentParagraph(prev => prev + 1);
          
          // Continue to next paragraph after a brief pause
          setTimeout(() => {
            if (currentParagraph + 1 < paragraphs.length) {
              teachNextParagraph();
            } else {
              onComplete?.();
            }
          }, 1000);
        };
        
        await audio.play();
      }
    } catch (error) {
      console.error("Auto-teach error:", error);
      setIsTeaching(false);
    }
  };
  
  // No visible UI
  return null;
}

/**
 * VoiceControl Component
 * 
 * Minimal voice control indicator (can be completely hidden)
 */
export function VoiceControl({ isListening, onToggle }) {
  if (!isListening) return null;
  
  return (
    <div
      style={{
        position: "fixed",
        bottom: "80px",
        right: "20px",
        background: "#ff4444",
        borderRadius: "50%",
        width: "40px",
        height: "40px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        zIndex: 9998,
        animation: "pulse 1s infinite"
      }}
      onClick={onToggle}
      title="Listening..."
    >
      <span style={{ fontSize: "1.2rem" }}>🎤</span>
    </div>
  );
}

export default InvisibleGuide;
export { THE_GUIDE, getVoice };
